// core
import { ReactNode } from "react";
import NumberFormat from "react-number-format";
import React from "react";

// interfaces
import { IBenefitResult } from "../interfaces/IBenefitResult";

// enums
import { BenefitType } from "../interfaces/enums/BenefitType";

// types
import { QuoteSettingContainerType } from "../interfaces/settings/QuoteSettingContainerType";

// shared
import {
    getBenefitTypeAsString,
    getPaymentPeriodAsString,
    getPremiumStructureAsString
} from "./enumHelpers";
import { ProductType } from "../interfaces/enums/ProductType";

function mapBenefitToProduct(benefit: BenefitType): ProductType {
    switch (benefit) {
        case BenefitType.LifeCover:
            return ProductType.Life;

        case BenefitType.TraumaCover:
            return ProductType.Trauma;

        case BenefitType.HealthCover:
        case BenefitType.SpecialistsAndTests:
        case BenefitType.GPCover:
        case BenefitType.DentalAndOptical:
            return ProductType.Health;

        case BenefitType.IncomeProtection:
            return ProductType.Income;

        case BenefitType.MortgageRepaymentCover:
            return ProductType.Mortgage;

        case BenefitType.WaiverOfPremium:
            return ProductType.WaiverOfPremium;
    }
}

export function getBenefitItem(
    benefitResult: IBenefitResult,
    settings: QuoteSettingContainerType[]
): ReactNode {
    const setting = settings.find(
        (s) => s.productType === mapBenefitToProduct(benefitResult.benefitType)
    );

    if (!setting) return null;

    switch (benefitResult.benefitType) {
        case BenefitType.LifeCover:
            if (setting.productType === ProductType.Life) {
                const settingsLife = setting.clients.find(
                    (c) => c.personId === benefitResult.personId
                );

                if (!settingsLife) return null;

                return (
                    <React.Fragment>
                        {`${getBenefitTypeAsString(benefitResult.benefitType)} (`}
                        <NumberFormat
                            value={settingsLife.coverAmount}
                            prefix={"$"}
                            displayType={"text"}
                            thousandSeparator
                            isNumericString
                            decimalScale={0}
                        />
                        {`), ${getPremiumStructureAsString(
                            settingsLife.premiumStructure
                        )}`}
                    </React.Fragment>
                );
            }
        case BenefitType.HealthCover:
        case BenefitType.SpecialistsAndTests:
        case BenefitType.GPCover:
        case BenefitType.DentalAndOptical:
            return (
                <React.Fragment>
                    {`${getBenefitTypeAsString(benefitResult.benefitType)} (`}
                    {benefitResult.excess > 0 && (
                        <NumberFormat
                            value={benefitResult.excess}
                            prefix={"$"}
                            displayType={"text"}
                            thousandSeparator
                            isNumericString
                            decimalScale={0}
                        />
                    )}
                    {benefitResult.excess <= 0 && "Nil"}
                    {` Excess)`}
                </React.Fragment>
            );
        case BenefitType.TraumaCover:
            if (setting.productType === ProductType.Trauma) {
                const settingsTrauma = setting.clients.find(
                    (c) => c.personId === benefitResult.personId
                );

                if (!settingsTrauma) return null;

                return (
                    <React.Fragment>
                        {`${settingsTrauma.traumaType} ${getBenefitTypeAsString(
                            benefitResult.benefitType
                        )} (`}
                        <NumberFormat
                            value={settingsTrauma.coverAmount}
                            prefix={"$"}
                            displayType={"text"}
                            thousandSeparator
                            isNumericString
                            decimalScale={0}
                        />
                        {`)`}
                    </React.Fragment>
                );
            }
        case BenefitType.IncomeProtection:
            if (setting.productType === ProductType.Income) {
                const incomeClient = setting.clients.find(
                    (c) => c.personId === benefitResult.personId
                );

                if (!incomeClient) return null;

                return (
                    <React.Fragment>
                        {`${getBenefitTypeAsString(benefitResult.benefitType)} (`}
                        <NumberFormat
                            value={incomeClient.monthlyCover}
                            prefix={"$"}
                            displayType={"text"}
                            thousandSeparator
                            isNumericString
                            decimalScale={0}
                        />
                        {` Monthly, ${
                            incomeClient.waitPeriodWeeks
                        } weeks wait, payment period ${getPaymentPeriodAsString(
                            incomeClient.paymentPeriod
                        ).toLowerCase()})`}
                    </React.Fragment>
                );
            }
        case BenefitType.MortgageRepaymentCover:
            if (setting.productType === ProductType.Mortgage) {
                const mortgageClient = setting.clients.find(
                    (c) => c.personId === benefitResult.personId
                );

                if (!mortgageClient) return null;

                return (
                    <React.Fragment>
                        {`${getBenefitTypeAsString(benefitResult.benefitType)} (`}
                        <NumberFormat
                            value={mortgageClient.monthlyCover}
                            prefix={"$"}
                            displayType={"text"}
                            thousandSeparator
                            isNumericString
                            decimalScale={0}
                        />
                        {` Monthly, ${
                            mortgageClient.waitPeriodWeeks
                        } weeks wait, payment period ${getPaymentPeriodAsString(
                            mortgageClient.paymentPeriod
                        ).toLowerCase()})`}
                    </React.Fragment>
                );
            }
        case BenefitType.WaiverOfPremium:
            if (setting.productType === ProductType.WaiverOfPremium) {
                const wopClient = setting.clients.find(
                    (c) => c.personId === benefitResult.personId
                );

                if (!wopClient) return null;

                return (
                    <React.Fragment>
                        {`${getBenefitTypeAsString(benefitResult.benefitType)} (${
                            wopClient.waitPeriodWeeks
                        } weeks wait)`}
                    </React.Fragment>
                );
            }
        default:
            return "Unidentified Benefit";
    }
}
